import LegalLayout from "./legal/LegalLayout";
import {
  LEGAL_APP_ORIGIN,
  LEGAL_BRAND,
  LEGAL_ENTITY,
  LEGAL_OPERATOR,
  LEGAL_PRIVACY_EMAIL,
  LEGAL_SERVICE_NAME,
  LEGAL_SUPPORT_EMAIL,
} from "./legal/legal-meta";

export default function PrivacyPolicyPage() {
  return (
    <LegalLayout
      title="Privacy Policy"
      lede={`This policy explains what information ${LEGAL_SERVICE_NAME} collects, why we collect it, and the choices schools, teachers, students, and families have. We built ${LEGAL_SERVICE_NAME} for the classroom, so protecting student information is a core part of the product, not an afterthought.`}
      sibling={{ label: "Terms of Service", to: "/terms" }}
    >
      <section className="legal-section">
        <h2>Who we are</h2>
        <p>
          {LEGAL_SERVICE_NAME} ("the Service") is operated by {LEGAL_ENTITY}, a Washington limited
          liability company doing business as {LEGAL_BRAND} ("we," "us," or "our"). The Service is a
          browser-based Python classroom for middle-school teachers and students,
          available at <code>{LEGAL_APP_ORIGIN}</code>. Student code runs locally in the browser; our
          servers act as the system of record for accounts, lessons, cohorts, and submitted work.
        </p>
      </section>

      <section className="legal-section">
        <h2>Our approach to student data</h2>
        <ul>
          <li>
            We use student information <strong>only</strong> to provide the educational Service to the
            school and teacher who invited the student.
          </li>
          <li>
            We do <strong>not</strong> sell personal information, and we do not use student data for
            advertising or to build advertising profiles.
          </li>
          <li>
            We do <strong>not</strong> use student work, names, or emails to train artificial-intelligence
            models.
          </li>
          <li>
            We collect the minimum information needed to run a classroom and keep accounts secure.
          </li>
        </ul>
      </section>

      <section className="legal-section">
        <h2>Information we collect</h2>
        <h3>Account and identity</h3>
        <p>
          You sign in with Google using your school Google account. From Google we receive your name,
          your school email address, and a Google account identifier so we know it is really you. We
          also store the role you choose on first sign-in (teacher or student).
        </p>

        <h3>Teacher-created content</h3>
        <p>
          For teacher accounts we store the courses, lessons, automated tests, cohorts, and
          join codes you create. This includes the prompts you type when you ask the lesson-authoring AI
          to draft or refine a lesson. If you create API keys for scripting, we store only a hashed
          version of the key, never the key itself.
        </p>

        <h3>Student work and progress</h3>
        <p>
          For student accounts we store which cohorts you have joined, the code and files in your lesson
          workspaces and playground projects, your submission attempts, the results of the automated
          checks those attempts run, and your progress status on each assignment.
        </p>

        <h3>Billing (teachers only)</h3>
        <p>
          Teacher subscriptions are processed by Stripe. We store your subscription status and the
          identifiers Stripe gives us (such as a customer and subscription ID) along with usage counters
          for paid features. We do <strong>not</strong> receive or store full payment-card numbers.
          Stripe handles payment information directly. Students are never charged.
        </p>

        <h3>Technical and operational data</h3>
        <p>
          To run and secure the Service we process standard technical information such as request logs,
          timestamps, and error diagnostics. We use this to keep the Service working, prevent abuse, and
          investigate security or safety problems.
        </p>
      </section>

      <section className="legal-section">
        <h2>How we use information</h2>
        <ul>
          <li>Authenticate users and keep accounts secure.</li>
          <li>
            Let teachers create lessons, assign them to cohorts, and review student progress and
            submissions.
          </li>
          <li>Let students open assignments, write and run code, and submit their work.</li>
          <li>Generate teacher lesson drafts when a teacher uses the AI authoring tools.</li>
          <li>Process teacher subscriptions and manage paid features.</li>
          <li>Operate, maintain, troubleshoot, and improve the Service.</li>
          <li>Detect, prevent, and respond to fraud, abuse, security incidents, and policy violations.</li>
          <li>Comply with our legal obligations.</li>
        </ul>
      </section>

      <section className="legal-section">
        <h2>Where your code runs and what reaches our servers</h2>
        <p>
          Python runs inside the student's own browser using an in-browser interpreter. Code is sent to
          our servers only when a student saves a workspace, submits an attempt, or saves a playground
          project, so that teachers can review work and so progress is not lost between devices.
        </p>
      </section>

      <section className="legal-section">
        <h2>Artificial-intelligence features</h2>
        <p>
          The lesson-authoring assistant is available to <strong>teachers</strong> and uses Amazon Web
          Services (AWS) Bedrock with Anthropic Claude models hosted in the United States. When a teacher
          uses it, only the teacher's lesson prompt and draft content are sent to the model. Student
          names, student emails, and student-written code are <strong>not</strong> sent to the AI.
        </p>
        <p>
          AWS Bedrock does not use prompts or responses to train its underlying models, and that content
          is not shared with model providers to improve their models. AI-generated drafts can be
          inaccurate; teachers review and edit them before using them with students.
        </p>
      </section>

      <section className="legal-section">
        <h2>Service providers we share data with</h2>
        <p>We share information only with the vendors that help us run the Service:</p>
        <ul>
          <li>
            <strong>Google</strong>: sign-in (we receive your name, school email, and account ID).
          </li>
          <li>
            <strong>Amazon Web Services (AWS)</strong>: hosting, database, file storage, and the
            lesson-authoring AI, in a United States region.
          </li>
          <li>
            <strong>Stripe</strong>: payment processing for teacher subscriptions.
          </li>
        </ul>
        <p>
          These providers may process information only to perform services for us. We may also disclose
          information if required by law, to comply with a valid legal request, or to protect the rights,
          property, or safety of students, teachers, the public, or {LEGAL_OPERATOR}, including to
          enforce our Terms of Service. We do not sell personal information.
        </p>
      </section>

      <section className="legal-section">
        <h2>Schools, FERPA, and children's privacy (COPPA)</h2>
        <p>
          {LEGAL_SERVICE_NAME} is designed to be used by schools and teachers in an educational setting,
          including with students who are under 13.
        </p>
        <ul>
          <li>
            <strong>School official / service provider.</strong> When a school or teacher uses the
            Service, we act on their behalf as a service provider. Student information is "education
            records" handled under the direction of the school or teacher, and used only to provide the
            Service, consistent with the Family Educational Rights and Privacy Act (FERPA) school
            official exception.
          </li>
          <li>
            <strong>Consent for younger students.</strong> For students under 13, we rely on the school
            or teacher to provide consent on behalf of parents for the school's educational use, as
            permitted under the Children's Online Privacy Protection Act (COPPA). We limit collection from
            students to what is needed for the classroom and do not condition participation on disclosing
            more than is reasonably necessary.
          </li>
          <li>
            <strong>No advertising or profiling.</strong> We do not use student data to advertise or to
            create profiles for any purpose other than providing the educational Service.
          </li>
        </ul>
        <p>
          Parents and guardians can review, request changes to, or request deletion of their child's
          information by working through their child's school or teacher, or by contacting us at{" "}
          <a href={`mailto:${LEGAL_PRIVACY_EMAIL}`}>{LEGAL_PRIVACY_EMAIL}</a>.
        </p>
      </section>

      <section className="legal-section">
        <h2>Data retention and deletion</h2>
        <p>
          We keep information while an account, cohort, or class is active and for as long as needed to
          provide the Service. Teachers can delete lessons and remove cohorts; student work is tied to
          the cohort it belongs to. When information is no longer needed for the educational purpose, or
          when a school, teacher, parent, or eligible student asks us to delete it, we delete or
          de-identify it within a reasonable period, except where we must retain it to meet a legal
          obligation, resolve disputes, or maintain security. To request access or deletion, contact{" "}
          <a href={`mailto:${LEGAL_PRIVACY_EMAIL}`}>{LEGAL_PRIVACY_EMAIL}</a>.
        </p>
      </section>

      <section className="legal-section">
        <h2>How we protect information</h2>
        <p>
          We use technical and organizational safeguards including encryption in transit (HTTPS),
          encryption of stored data, hashed API keys, and access controls that limit who can reach
          student data. No online service can be completely secure, but we work to protect your
          information and to respond promptly if a problem occurs.
        </p>
      </section>

      <section className="legal-section">
        <h2>Your choices and rights</h2>
        <p>
          Depending on where you live, you may have rights to access, correct, or delete personal
          information, or to limit certain processing. For student records, these requests are normally
          made through the school or teacher. You can exercise rights or ask questions any time by
          emailing <a href={`mailto:${LEGAL_PRIVACY_EMAIL}`}>{LEGAL_PRIVACY_EMAIL}</a>. You can also stop
          using the Service and ask that your account be closed.
        </p>
      </section>

      <section className="legal-section">
        <h2>Changes to this policy</h2>
        <p>
          We may update this policy as the Service evolves or the law changes. We will revise the "Last
          updated" date above, and for material changes affecting student data we will make reasonable
          efforts to notify teachers and schools before the change takes effect.
        </p>
      </section>

      <section className="legal-section">
        <h2>Contact us</h2>
        <p>
          Questions about privacy? Email{" "}
          <a href={`mailto:${LEGAL_PRIVACY_EMAIL}`}>{LEGAL_PRIVACY_EMAIL}</a>. For general help, email{" "}
          <a href={`mailto:${LEGAL_SUPPORT_EMAIL}`}>{LEGAL_SUPPORT_EMAIL}</a>.
        </p>
      </section>
    </LegalLayout>
  );
}
